import { useReducer } from 'react'
import { useTranslation } from 'react-i18next'
import { v4 } from 'uuid'
import { capitalizeFirstLetter, getLastPathElement } from '../../../../util/StringUtil'
import initDateStandards from '../../../common/DmcDateStandards'
import { initPlaceStandards } from '../../../common/DmcPlaceStandards'
import { deepCopy } from '../../../../util/Utils'
import useNotifications from '../../../../hooks/useNotifications'
import { getPerson, addCtrs } from '../../../../services/personService'
import { makeNewDateConclusion, makeNewPlaceConclusion } from '../../PersonUtils'

export default () => {}

const actions = {
  SET_FACT_TYPE: 'setFactType',
  SET_FACT_DATE_STANDARDS: 'setFactDateStandards',
  SET_FACT_PLACE_STANDARDS: 'setFactPlaceStandards',
  ADD_FACT: 'addFact',
  UPDATE_FACT: 'updateFact',
  DELETE_FACT: 'deleteFact',
  ADD_NAME_PART: 'addNamePart',
  DELETE_NAME_PART: 'deleteNamePart',
  SET_NAME_VALUE: 'setNameValue',
  SET_NAME_TYPE: 'setNameType',
  CLEAR_ORDINANCES_LIST: 'clearOrdinancesList',
  CLEAR_ORDINANCE_FIELDS: 'clearOrdinanceFields',
  READ_NEW_PID: 'readNewPID',
  SET_FIELDS_FOR_NEXT_ORDINANCE: 'setFieldsForNextOrdinance',
  SET_MARRIAGE_DATE_STANDARDS: 'setMarriageDateStandards',
  SET_MARRIAGE_PLACE_STANDARDS: 'setMarriagePlaceStandards',
  SET_PID: 'setPID',
  SET_ORDINANCE_TYPE: 'setOrdinanceType',
  SET_TEMPLE: 'setTemple',
  SET_ORDINANCE_DATE_STANDARDS: 'setOrdinanceDateStandards',
  ADD_ORDINANCE_TO_LIST: 'pushOrdinance',
}

export function useCreateCtrController(currentPerson, refetchOrdinances) {
  const { error } = useNotifications()
  const { t } = useTranslation()

  const CREATE_CTR_ORDINANCE_TYPES = [
    {
      value: 'http://api.familysearch.org/temple/BAPTISM_LDS',
      label: t('ctr.ordinanceType.BAPTISM_LDS', 'Baptism'),
    },
    {
      value: 'http://api.familysearch.org/temple/CONFIRMATION_LDS',
      label: t('ctr.ordinanceType.CONFIRMATION_LDS', 'LDS Confirmation'),
    },
    {
      value: 'http://api.familysearch.org/temple/INITIATORY',
      label: t('ctr.ordinanceType.INITIATORY', 'Initiatory'),
    },
    {
      value: 'http://api.familysearch.org/temple/ENDOWMENT',
      label: t('ctr.ordinanceType.ENDOWMENT', 'Endowment'),
    },
    {
      value: 'http://api.familysearch.org/temple/SEALING_TO_PARENTS',
      label: t('ctr.ordinanceType.SEALING_TO_PARENTS', 'Sealing to Parents'),
    },
    {
      value: 'http://api.familysearch.org/temple/SEALING_TO_SPOUSE',
      label: t('ctr.ordinanceType.SEALING_TO_SPOUSE', 'Sealing to Spouse'),
    },
  ]

  const validFactTypes = [
    { value: '', label: '' },
    {
      label: t('birth', 'Birth'),
      value: 'http://gedcomx.org/Birth',
    },
    {
      label: t('christening', 'Christening'),
      value: 'http://gedcomx.org/Christening',
    },
    {
      label: t('marriage', 'Marriage'),
      value: 'http://gedcomx.org/Marriage',
    },
    {
      label: t('death', 'Death'),
      value: 'http://gedcomx.org/Death',
    },
    {
      label: t('burial', 'Burial'),
      value: 'http://gedcomx.org/Burial',
    },
  ]

  const initialState = {
    ordinances: [],
    ordinance: {
      type: null,
      temple: null,
      dateStandards: initDateStandards(),
    },
    principal: makeNewPerson(
      t('principal', 'Principal'),
      currentPerson?.id,
      currentPerson?.gender,
      currentPerson?.living,
      currentPerson?.factConclusions,
      currentPerson?.nameConclusion?.value?.nameForms?.[0]?.parts
    ),
    father: makeNewPerson(
      t('father', 'Father'),
      currentPerson?.relationships?.parents?.[0]?.parent1?.personId,
      currentPerson?.relationships?.parents?.[0]?.parent1?.gender,
      currentPerson?.relationships?.parents?.[0]?.parent1?.living,
      [],
      currentPerson?.relationships?.parents?.[0]?.parent1?.nameForms?.[0]?.parts
    ),
    mother: makeNewPerson(
      t('mother', 'Mother'),
      currentPerson?.relationships?.parents?.[0]?.parent2?.personId,
      currentPerson?.relationships?.parents?.[0]?.parent2?.gender,
      currentPerson?.relationships?.parents?.[0]?.parent2?.living,
      [],
      currentPerson?.relationships?.parents?.[0]?.parent2?.nameForms?.[0]?.parts
    ),
    spouse: makeNewPerson(
      t('spouse', 'Spouse'),
      currentPerson?.relationships?.spouses?.[0]?.spouse?.personId,
      currentPerson?.relationships?.spouses?.[0]?.spouse?.gender,
      currentPerson?.relationships?.spouses?.[0]?.spouse?.living,
      [],
      currentPerson?.relationships?.spouses?.[0]?.spouse?.nameForms?.[0]?.parts
    ),
    marriage: {
      dateStandards: initDateStandards(getMarriageFact()?.value?.date),
      placeStandards: initPlaceStandards(
        getMarriageFact()?.value?.place?.original,
        getMarriageFact()?.value?.place?.names?.[0]?.value,
        getMarriageFact()?.value?.place?.id
      ),
    },
    controls: {
      addToListBtnDisabled: true,
      ordinanceTypes: CREATE_CTR_ORDINANCE_TYPES,
    },
  }

  const reducer = (oldState, action) => {
    const newState = deepCopy(oldState)
    if (action.type === actions.SET_FACT_TYPE) {
      const personToEdit = getPersonToEdit(action.payload.personHeading, newState)
      personToEdit.factsComponentsStateMap[action.payload.conclusionId].type = action.payload.type
      personToEdit.factsMap[action.payload.conclusionId].value.type = action.payload.type
    } else if (action.type === actions.SET_FACT_DATE_STANDARDS) {
      const personToEdit = getPersonToEdit(action.payload.personHeading, newState)
      personToEdit.factsComponentsStateMap[action.payload.conclusionId].date = action.payload.dateStandards
      personToEdit.factsMap[action.payload.conclusionId].value.date = makeNewDateConclusion(
        action.payload.dateStandards.originalText,
        action.payload.dateStandards.standard?.standardizedText
      )
    } else if (action.type === actions.SET_FACT_PLACE_STANDARDS) {
      const personToEdit = getPersonToEdit(action.payload.personHeading, newState)
      personToEdit.factsComponentsStateMap[action.payload.conclusionId].place = action.payload.placeStandards
      personToEdit.factsMap[action.payload.conclusionId].value.place = makeNewPlaceConclusion(
        action.payload.placeStandards.originalText,
        action.payload.placeStandards.standard?.standardizedText,
        action.payload.placeStandards.standard?.id
      )
    } else if (action.type === actions.ADD_FACT) {
      const personToEdit = getPersonToEdit(action.payload.personHeading, newState)
      personToEdit.factsMap[action.payload.conclusionId] = action.payload.conclusionFact
      personToEdit.factsComponentsStateMap[action.payload.conclusionId] = conclusionFactToComponentsState(
        action.payload.conclusionFact
      )
    } else if (action.type === actions.UPDATE_FACT) {
      const personToEdit = getPersonToEdit(action.payload.personHeading, newState)
      personToEdit.factsMap[action.payload.conclusionId] = action.payload.conclusionFact
      personToEdit.factsComponentsStateMap[action.payload.conclusionId] = conclusionFactToComponentsState(
        action.payload.conclusionFact
      )
    } else if (action.type === actions.DELETE_FACT) {
      const personToEdit = getPersonToEdit(action.payload.personHeading, newState)
      delete personToEdit.factsMap[action.payload.conclusionId]
      delete personToEdit.factsComponentsStateMap[action.payload.conclusionId]
    } else if (action.type === actions.ADD_NAME_PART) {
      const personToEdit = getPersonToEdit(action.payload.personHeading, newState)
      personToEdit.nameParts.push(makeNewNamePart())
    } else if (action.type === actions.DELETE_NAME_PART) {
      const personToEdit = getPersonToEdit(action.payload.personHeading, newState)
      personToEdit.nameParts.splice(action.payload.nameIndex, 1)
    } else if (action.type === actions.SET_NAME_VALUE) {
      const personToEdit = getPersonToEdit(action.payload.personHeading, newState)
      personToEdit.nameParts[action.payload.nameIndex].value = action.payload.nameValue
    } else if (action.type === actions.SET_NAME_TYPE) {
      const personToEdit = getPersonToEdit(action.payload.personHeading, newState)
      personToEdit.nameParts[action.payload.nameIndex].type = action.payload.nameType
    } else if (action.type === actions.CLEAR_ORDINANCES_LIST) {
      newState.ordinances = []
      newState.controls.ordinanceTypes = CREATE_CTR_ORDINANCE_TYPES
    } else if (action.type === actions.CLEAR_ORDINANCE_FIELDS) {
      newState.ordinance.type = null
      newState.ordinance.temple = null
      newState.ordinance.dateStandards = initDateStandards()
    } else if (action.type === actions.READ_NEW_PID) {
      const personToEdit = getPersonToEdit(action.payload.personHeading, newState)
      personToEdit.gender = capitalizeFirstLetter(action.payload.personData?.gender.toLowerCase())
      personToEdit.living = action.payload.personData.living
      personToEdit.factsMap = factConclusionsToFactsMap(action.payload.personData?.factConclusions)
      personToEdit.factsComponentsStateMap = factConclusionsToComponentsStateMap(
        action.payload.personData?.factConclusions
      )
      personToEdit.nameParts = action.payload.personData?.nameConclusion?.value?.nameForms?.[0]?.parts
    } else if (action.type === actions.SET_MARRIAGE_DATE_STANDARDS) {
      newState.marriage.dateStandards = action.payload.dateStandards
    } else if (action.type === actions.SET_MARRIAGE_PLACE_STANDARDS) {
      newState.marriage.placeStandards = action.payload.placeStandards
    } else if (action.type === actions.SET_PID) {
      const personToEdit = getPersonToEdit(action.payload.personHeading, newState)
      personToEdit.id = action.payload.pid
    } else if (action.type === actions.SET_ORDINANCE_TYPE) {
      newState.ordinance.type = action.payload.ordinanceType
    } else if (action.type === actions.SET_TEMPLE) {
      newState.ordinance.temple = action.payload.temple
    } else if (action.type === actions.SET_ORDINANCE_DATE_STANDARDS) {
      newState.ordinance.dateStandards = action.payload.dateStandards
    } else if (action.type === actions.SET_ADD_TO_LIST_BTN_DISABLED) {
      newState.controls.addToListBtnDisabled = action.payload.disabled
    } else if (action.type === actions.SET_FIELDS_FOR_NEXT_ORDINANCE) {
      newState.ordinance.type = newState.controls.ordinanceTypes[0]?.value
    } else if (action.type === actions.ADD_ORDINANCE_TO_LIST) {
      newState.ordinances.push(action.payload.ordinance)
      newState.controls.ordinanceTypes = newState.controls.ordinanceTypes.filter((ordinanceType) => {
        if (newState.ordinances.some((ordinance) => getLastPathElement(ordinance.type) === 'SEALING_TO_SPOUSE')) {
          return false
        }
        if (getLastPathElement(ordinanceType.value) === 'SEALING_TO_SPOUSE') {
          return newState.ordinances.length === 0
        }
        return !newState.ordinances.some((ordinance) => ordinance.type === ordinanceType.value)
      })
    } else {
      error('Dispatch Action not defined')
    }
    return newState
  }
  const [createCtrState, createCtrDispatch] = useReducer(reducer, initialState)

  const createCtrActions = {
    setFactType: (personHeading, conclusionId, type) => {
      createCtrDispatch({
        type: actions.SET_FACT_TYPE,
        payload: {
          personHeading,
          conclusionId,
          type,
        },
      })
    },
    setFactDateStandards: (personHeading, conclusionId, dateStandards) => {
      createCtrDispatch({
        type: actions.SET_FACT_DATE_STANDARDS,
        payload: {
          personHeading,
          conclusionId,
          dateStandards,
        },
      })
    },
    setFactPlaceStandards: (personHeading, conclusionId, placeStandards) => {
      createCtrDispatch({
        type: actions.SET_FACT_PLACE_STANDARDS,
        payload: {
          personHeading,
          conclusionId,
          placeStandards,
        },
      })
    },
    addFact: (personHeading, conclusionFact) => {
      createCtrDispatch({
        type: actions.ADD_FACT,
        payload: {
          personHeading,
          conclusionFact,
          conclusionId: conclusionFact.conclusionId,
        },
      })
    },
    updateFact: (personHeading, conclusionFact) => {
      createCtrDispatch({
        type: actions.UPDATE_FACT,
        payload: {
          personHeading,
          conclusionFact,
          conclusionId: conclusionFact.conclusionId,
        },
      })
    },
    deleteFact: (personHeading, conclusionId) => {
      createCtrDispatch({
        type: actions.DELETE_FACT,
        payload: {
          personHeading,
          conclusionId,
        },
      })
    },
    addNamePart: (personHeading) => {
      createCtrDispatch({
        type: actions.ADD_NAME_PART,
        payload: {
          personHeading,
        },
      })
    },
    deleteNamePart: (personHeading, nameIndex) => {
      createCtrDispatch({
        type: actions.DELETE_NAME_PART,
        payload: {
          personHeading,
          nameIndex,
        },
      })
    },
    setNameValue: (personHeading, nameIndex, nameValue) => {
      createCtrDispatch({
        type: actions.SET_NAME_VALUE,
        payload: {
          personHeading,
          nameIndex,
          nameValue,
        },
      })
    },
    setNameType: (personHeading, nameIndex, nameType) => {
      createCtrDispatch({
        type: actions.SET_NAME_TYPE,
        payload: {
          personHeading,
          nameIndex,
          nameType,
        },
      })
    },
    clearOrdinancesList: () => {
      createCtrDispatch({
        type: actions.CLEAR_ORDINANCES_LIST,
      })
    },
    readNewPID: (personHeading, personId) => {
      return getPerson(personId)
        .then((personData) => {
          createCtrDispatch({
            type: actions.READ_NEW_PID,
            payload: {
              personHeading,
              personData,
            },
          })
          return Promise.resolve()
        })
        .catch((err) => {
          error(`Failed to retrieve person summary with error ${err.response.statusText}`, err)
          return Promise.resolve() // This means that the error has been handled
        })
    },
    setMarriageDateStandards: (dateStandards) => {
      createCtrDispatch({
        type: actions.SET_MARRIAGE_DATE_STANDARDS,
        payload: {
          dateStandards,
        },
      })
    },
    setMarriagePlaceStandards: (placeStandards) => {
      createCtrDispatch({
        type: actions.SET_MARRIAGE_PLACE_STANDARDS,
        payload: {
          placeStandards,
        },
      })
    },
    setPID: (personHeading, pid) => {
      createCtrDispatch({
        type: actions.SET_PID,
        payload: {
          personHeading,
          pid,
        },
      })
    },
    setOrdinanceType: (ordinanceType) => {
      createCtrDispatch({
        type: actions.SET_ORDINANCE_TYPE,
        payload: {
          ordinanceType,
        },
      })
    },
    setTemple: (temple) => {
      createCtrDispatch({
        type: actions.SET_TEMPLE,
        payload: {
          temple,
        },
      })
    },
    setOrdinanceDateStandards: (dateStandards) => {
      createCtrDispatch({
        type: actions.SET_ORDINANCE_DATE_STANDARDS,
        payload: {
          dateStandards,
        },
      })
    },
    submitList: () => {
      return addCtrs(createCtrState.ordinances)
        .then(() => {
          createCtrDispatch({
            type: actions.CLEAR_ORDINANCES_LIST,
          })
          createCtrDispatch({
            type: actions.CLEAR_ORDINANCE_FIELDS,
          })
          refetchOrdinances()
          return Promise.resolve()
        })
        .catch((err) => {
          if (typeof err?.response?.data === 'object') {
            error(t('person.ordinances.errors.creatingOrdinancesBase', 'Error creating ordinances'), err)
          } else {
            error(
              t('person.ordinances.errors.creatingOrdinances', {
                defaultValue: `Error creating ordinances. Try complete dates (dd MMMM yyyy). ${err?.message} ${err?.response?.data}`,
                message: err?.message,
                response: err?.response?.data,
              }),
              err
            )
          }
          return Promise.resolve() // This means that the error has been handled
        })
    },
    addOrdinanceToList: () => {
      const ordinance = {
        type: createCtrState.ordinance.type,
        performedDate: makeNewDateConclusion(
          createCtrState.ordinance.dateStandards?.original,
          createCtrState.ordinance.dateStandards?.primaryText
        ),
        temple: createCtrState.ordinance.temple,
        principal: {
          id: createCtrState.principal?.id,
          gender: createCtrState.principal.gender,
          name: currentPerson.name,
          nameParts: createCtrState.principal.nameParts,
          facts: factsMapToFactConclusions(createCtrState.principal.factsMap),
        },
        father: {
          id: createCtrState.father.id,
          gender: createCtrState.father?.gender,
          nameParts: createCtrState.father.nameParts,
          facts: factsMapToFactConclusions(createCtrState.father.factsMap),
        },
        mother: {
          id: createCtrState.mother.id,
          gender: createCtrState.mother?.gender,
          nameParts: createCtrState.mother.nameParts,
          facts: factsMapToFactConclusions(createCtrState.mother.factsMap),
        },
        spouse: {
          id: createCtrState.spouse.id,
          gender: createCtrState.spouse?.gender,
          nameParts: createCtrState.spouse.nameParts,
          facts: factsMapToFactConclusions(createCtrState.spouse.factsMap),
          marriage: {
            value: {
              type: 'http://gedcomx.org/Marriage',
              date: makeNewDateConclusion(
                createCtrState?.marriage?.dateStandards?.originalText,
                createCtrState?.marriage?.dateStandards?.standard?.standardizedText
              ),
              place: makeNewPlaceConclusion(
                createCtrState?.marriage?.placeStandards?.originalText,
                createCtrState?.marriage?.placeStandards?.standard?.standardizedText,
                createCtrState?.marriage?.placeStandards?.standard?.id
              ),
            },
          },
        },
      }
      createCtrDispatch({
        type: actions.ADD_ORDINANCE_TO_LIST,
        payload: {
          ordinance,
        },
      })
      createCtrDispatch({
        type: actions.SET_FIELDS_FOR_NEXT_ORDINANCE,
        payload: {
          ordinanceType: ordinance.type,
        },
      })
    },
  }

  /**
   * Helper Functions
   * */

  // fact conclusions to fact map functions

  function factConclusionsToFactsMap(factConclusions) {
    const factsMap = {}
    factConclusions
      ?.filter((factConclusion) => {
        return validFactTypes.some(
          (factType) => factType.value.toLowerCase() === factConclusion.value?.type?.toLowerCase()
        )
      })
      .forEach((factConclusion) => {
        factsMap[factConclusion.conclusionId] = factConclusion
      })
    return factsMap
  }

  function conclusionFactToComponentsState(factConclusion) {
    return {
      type: factConclusion?.value?.type,
      date: initDateStandards(factConclusion?.value?.date),
      place: initPlaceStandards(
        factConclusion?.value?.place?.original,
        factConclusion?.value?.place?.names?.[0]?.value,
        factConclusion?.value?.place?.id
      ),
    }
  }

  function factConclusionsToComponentsStateMap(factConclusions) {
    const componentStateMap = {}
    factConclusions
      ?.filter((factConclusion) => {
        return validFactTypes.some(
          (factType) => factType.value.toLowerCase() === factConclusion.value?.type?.toLowerCase()
        )
      })
      .forEach((factConclusion) => {
        componentStateMap[factConclusion.conclusionId] = conclusionFactToComponentsState(factConclusion)
      })
    return componentStateMap
  }

  function factsMapToFactConclusions(factsMap) {
    factsMap = factsMap || {}
    return Object.keys(factsMap)
      .filter((conclusionFactId) => {
        const factConclusion = factsMap[conclusionFactId]
        return validFactTypes.some(
          (factType) => factType.value.toLowerCase() === factConclusion.value?.type?.toLowerCase()
        )
      })
      .map((factKey) => {
        return factsMap[factKey]
      })
  }

  // functions that help cleaning up code

  function makeNewPerson(heading, id, gender, living, factConclusions, nameParts) {
    return {
      heading: heading || '',
      id: id || '',
      gender: capitalizeFirstLetter(gender?.toLowerCase()),
      living: living || false,
      factsMap: factConclusionsToFactsMap(factConclusions),
      factsComponentsStateMap: factConclusionsToComponentsStateMap(factConclusions),
      nameParts: nameParts || [],
    }
  }

  function makeNewNamePart() {
    return { id: v4(), type: null, value: null }
  }

  // functions that help finding stuff

  function getMarriageFact() {
    const marriageFacts = currentPerson.relationships?.spouses?.[0]?.facts?.filter(
      (factConclusion) => getLastPathElement(factConclusion.value?.type) === 'Marriage'
    )
    return marriageFacts && marriageFacts[0]
  }

  function getPersonToEdit(heading, newState) {
    let person = null
    switch (heading) {
      case t('father', 'Father'): {
        person = newState.father
        break
      }
      case t('mother', 'Mother'): {
        person = newState.mother
        break
      }
      case t('spouse', 'Spouse'): {
        person = newState.spouse
        break
      }
      case t('principal', 'Principal'): {
        person = newState.principal
        break
      }
      default: {
        break
      }
    }
    return person
  }

  return { createCtrState, createCtrActions }
}
